import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";

export interface ContractorAvailabilityRow {
  date: string;         // "YYYY-MM-DD"
  is_available: boolean;
  note: string | null;
  installer_name: string | null;
}

export interface ContractorAvailabilityBatchRow extends ContractorAvailabilityRow {
  contractor_id: string;
}

/**
 * Fetch all availability rows for one contractor within [from, to] (inclusive).
 * Used by the dispatch calendar to overlay day cells.
 */
export async function fetchContractorAvailability(
  contractorId: string,
  from: string,
  to: string,
): Promise<ContractorAvailabilityRow[]> {
  const orgId = await getCurrentOrganizationId();
  if (!orgId) return [];

  const { data, error } = await supabase
    .from("contractor_availability")
    .select("date, is_available, note, installer_name")
    .eq("contractor_id", contractorId)
    .eq("organization_id", orgId)
    .eq("status", "approved")
    .gte("date", from)
    .lte("date", to);

  if (error) {
    console.error("[contractor-availability] fetchContractorAvailability:", error);
    return [];
  }
  return data ?? [];
}

/**
 * Fetch all availability rows for the org within [from, to] (inclusive).
 * Used by the dispatch calendar to overlay day cells without needing to
 * select a specific contractor — since only one contractor sets availability
 * at a time, each date will have at most one signal.
 */
export async function fetchOrgContractorAvailability(
  from: string,
  to: string,
): Promise<ContractorAvailabilityBatchRow[]> {
  // Note: organization_id is intentionally excluded from this filter because
  // the portal currently writes rows with a placeholder org ID
  // (00000000-0000-0000-0000-000000000001). Scoping is provided by the
  // contractor_id FK which references the org-scoped contractors table.
  const { data, error } = await supabase
    .from("contractor_availability")
    .select("contractor_id, date, is_available, note, installer_name")
    .eq("status", "approved")
    .gte("date", from)
    .lte("date", to);

  if (error) {
    console.error("[contractor-availability] fetchOrgContractorAvailability:", error);
    return [];
  }
  return data ?? [];
}

/**
 * Fetch availability rows for multiple contractors on a single date.
 * Used before dispatching an offer to check if any contractor is unavailable.
 */
export async function fetchContractorAvailabilityBatch(
  contractorIds: string[],
  date: string,
): Promise<ContractorAvailabilityBatchRow[]> {
  if (contractorIds.length === 0) return [];

  const orgId = await getCurrentOrganizationId();
  if (!orgId) return [];

  const { data, error } = await supabase
    .from("contractor_availability")
    .select("contractor_id, date, is_available, note, installer_name")
    .eq("organization_id", orgId)
    .eq("status", "approved")
    .eq("date", date)
    .in("contractor_id", contractorIds);

  if (error) {
    console.error("[contractor-availability] fetchContractorAvailabilityBatch:", error);
    return [];
  }
  return data ?? [];
}
